import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { TopupSummary } from "@/services/topup.service";
import { useAuthStore } from "@/stores/auth.store";
import type { SummaryData } from "@/types/topup/summary";
import { toServerDate } from "@/utils/date";

const POLL_MS = 5000; // production: 10000, dev: 3000
const MAX_ATTEMPTS = 10;

const PENDING = ["pending_payment"] as const;

const isExpired = (value?: string | null) => {
  const date = toServerDate(value);
  return !!date && date.getTime() <= Date.now();
};

export function useTopupConfirmation(topupno?: string) {
  const [topupSummary, setTopupSummary] = useState<SummaryData | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [loading, setLoading] = useState(Boolean(topupno));
  const [notFound, setNotFound] = useState(!topupno);
  const [checking, setChecking] = useState(false);

  const [retryKey, setRetryKey] = useState(0);

  // Prevent unnecessary duplicated retry state.
  const topupSummaryRef = useRef<SummaryData | null>(null);

  useEffect(() => {
    if (!topupno) return;

    let active = true;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const isPending = (status?: string) =>
      (PENDING as readonly string[]).includes(status ?? "");

    const finishTimeout = () => {
      if (!active) return;

      setLoading(false);
      setChecking(false);
      setTimedOut(true);
    };

    const fetchTopupSummary = async () => {
      if (!active) return;

      attempts += 1;

      try {
        const agentCompanySellerId =
          useAuthStore.getState().user?.agentCompanySellerId ?? "";

        const response = await TopupSummary({
          agentCompanySellerId,
          topUpNo: topupno,
        });

        if (!active) return;

        const result = response?.data;

        // No top-up returned
        if (!result) {
          if (attempts === 1) {
            setLoading(false);
            setChecking(false);
            setNotFound(true);
            return;
          }

          if (attempts >= MAX_ATTEMPTS) {
            finishTimeout();
            return;
          }

          timer = setTimeout(fetchTopupSummary, POLL_MS);
          return;
        }

        // Top-up found
        topupSummaryRef.current = result;

        setTopupSummary(result);
        setLoading(false);
        setNotFound(false);

        // Adjust this field if your SummaryData uses another status property.
        const status = result.paymentStatus;

        // Still waiting for payment — continue polling.
        if (isPending(status)) {
          // Payment window already expired.
          if (isExpired(result.paymentExpiredDateTime)) {
            finishTimeout();
            return;
          }

          timer = setTimeout(fetchTopupSummary, POLL_MS);
          return;
        }

        // Final status
        setChecking(false);
        setTimedOut(false);
      } catch (error) {
        if (!active) return;

        if (attempts >= MAX_ATTEMPTS) {
          setLoading(false);
          setChecking(false);
          setTimedOut(true);

          toast.error(
            error instanceof Error
              ? error.message
              : "Failed to fetch top-up summary",
          );

          return;
        }

        timer = setTimeout(fetchTopupSummary, POLL_MS);
      }
    };

    fetchTopupSummary();

    return () => {
      active = false;

      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [topupno, retryKey]);

  // Fire exactly at the payment expiry moment.
  useEffect(() => {
    if (topupSummary?.paymentStatus !== "pending") return;

    const date = toServerDate(topupSummary.paymentExpiredDateTime);

    if (!date) return;

    const ms = Math.max(0, date.getTime() - Date.now());

    const timer = setTimeout(() => {
      setTimedOut(true);
    }, ms);

    return () => clearTimeout(timer);
  }, [topupSummary]);

  const checkStatus = useCallback(() => {
    if (!topupno || checking) return;

    setTimedOut(false);
    setLoading(true);
    setChecking(true);
    setNotFound(false);

    setRetryKey((current) => current + 1);
  }, [topupno, checking]);

  return {
    topupSummary,
    timedOut,
    notFound,
    loading,
    checking,
    checkStatus,
  };
}